import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { cachedGet } from 'Utils/cachedGet';
import { getFlowFolderPath } from 'Shared/flows/folders/folderUtils';

const FlowSearch = ({ value, onChange, onSelect, onFind, folders, sources }) => {
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const sourcesKey = sources.join('|');

    const search = useCallback((text) => {
        const query = `pagination=${encodeURIComponent(JSON.stringify({ pageSize: 20, currentPage: 0 }))}&search=${encodeURIComponent(text)}&folder_id=any`;

        return Promise.all(sourcesKey.split('|').map((source) => cachedGet(`${source}${source.includes('?') ? '&' : '?'}${query}`)
            .then((res) => res.data.flows || [])
            .catch(() => [])))
            .then((lists) => lists.flat().filter((flow, i, all) => all.findIndex((f) => f.id === flow.id) === i));
    }, [sourcesKey]);

    useEffect(() => {
        const text = value.trim();
        if (text.length < 2) {
            setOptions([]);
            return undefined;
        }
        const timer = setTimeout(() => search(text).then(setOptions), 300);
        return () => clearTimeout(timer);
    }, [value, search]);

    const find = () => {
        setOpen(false);
        onFind(value.trim());
    };

    return (
        <>
            <Autocomplete
                freeSolo
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                options={options}
                filterOptions={(list) => list}
                getOptionLabel={(flow) => (typeof flow === 'string' ? flow : flow.name)}
                inputValue={value}
                onInputChange={(e, text) => onChange(text)}
                onChange={(e, flow) => (typeof flow === 'string' ? find() : flow && onSelect(flow))}
                renderOption={(props, flow) => (
                    <li {...props} key={flow.id}>
                        <Box>
                            <Typography color="text.primary">{flow.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {[flow.external_id, getFlowFolderPath(folders, flow.id)].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    </li>
                )}
                sx={{ minWidth: 300 }}
                renderInput={(params) => <TextField {...params} label="Search Flows" size="small" />}
            />
            <Button variant="contained" onClick={find}>Find</Button>
        </>
    );
};

export default FlowSearch;
